<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import MdEditor from '../components/MdEditor.vue'
import { 
  getCatList, 
  getNoteList, 
  addNote, 
  editNote, 
  deleteNote,
  getNoteDetail,
  addCat,
  editCat,
  deleteCat,
  getTagList,
  bindTags,
  type VentureNoteCat,
  type VentureNote,
  type VentureNoteTag
} from '../api/venture-note'

// 分类列表
const categories = ref<VentureNoteCat[]>([])
const activeCatId = ref(0) // 0 表示全部
const sidebarVisible = ref(true) // 分类栏显示状态

// 文章列表
const notes = ref<VentureNote[]>([])
const loading = ref(false)
const isCatLoading = ref(false) // 分类列表加载中
const page = ref(1)
const pageSize = ref(10)
const total = ref(0)

// 标签列表
const tags = ref<VentureNoteTag[]>([])

// 当前选中的文章（用于右侧编辑区）
const selectedNote = ref<VentureNote | null>(null)
const isEditing = ref(false)

// 编辑表单
const editingNote = ref<Partial<VentureNote>>({
  title: '',
  description: '',
  content: '',
  category_id: 0,
  tag_ids: []
})
const selectedTagIds = ref<number[]>([])
const isSaving = ref(false)
const isNewArticle = ref(false) // 是否新建模式
const isNoteLoading = ref(false) // 文章详情加载中

// 编辑弹窗（用于新建）
const showEditModal = ref(false)

// 分类管理弹窗
const showCatModal = ref(false)
const editingCat = ref<Partial<VentureNoteCat>>({
  name: '',
  description: '',
  sort: 0,
  status: 1
})
const isSavingCat = ref(false)
const editingCatId = ref<number | null>(null)

// 删除确认
const showDeleteConfirm = ref(false)
const deletingNoteId = ref<number | null>(null)
const showDeleteCatConfirm = ref(false)
const deletingCatId = ref<number | null>(null)

// Toast 提示
const toastMessage = ref('')
const showToast = ref(false)
const toastTimer = ref<number | null>(null)

const showToastMessage = (message: string) => {
  toastMessage.value = message
  showToast.value = true
  if (toastTimer.value) {
    clearTimeout(toastTimer.value)
  }
  toastTimer.value = window.setTimeout(() => {
    showToast.value = false
  }, 3000)
}

// 右键菜单
const contextMenu = ref({
  show: false,
  x: 0,
  y: 0,
  note: null as VentureNote | null
})

// 显示右键菜单
const showContextMenu = (event: MouseEvent, note: VentureNote) => {
  event.preventDefault()
  contextMenu.value = {
    show: true,
    x: event.clientX,
    y: event.clientY,
    note
  }
}

// 隐藏右键菜单
const hideContextMenu = () => {
  contextMenu.value.show = false
  contextMenu.value.note = null
}

// 右键菜单 - 编辑
const contextMenuEdit = () => {
  if (contextMenu.value.note) {
    selectNote(contextMenu.value.note)
    startEdit()
    hideContextMenu()
  }
}

// 右键菜单 - 删除
const contextMenuDelete = () => {
  if (contextMenu.value.note) {
    confirmDelete(contextMenu.value.note.id)
    hideContextMenu()
  }
}

// 右键菜单 - 切换可见性
const contextMenuToggleStatus = async () => {
  if (!contextMenu.value.note) return
  
  const note = contextMenu.value.note
  const newStatus = note.status === 1 ? 0 : 1
  
  try {
    const res = await editNote({
      id: note.id,
      status: newStatus
    })
    if (res.code === 1) {
      loadArticles()
    } else {
      alert(res.msg || '操作失败')
    }
  } catch (error) {
    console.error('切换状态失败:', error)
    alert('操作失败')
  }
  hideContextMenu()
}

// 点击页面其他地方隐藏右键菜单
onMounted(() => {
  document.addEventListener('click', hideContextMenu)
  document.addEventListener('click', hideCatContextMenu)
})

// 计算属性：一级分类（直接使用返回的树形数据）
const parentCategories = computed(() => categories.value)

// 获取分类标题（递归查找）
const getCategoryTitle = (catId: number): string => {
  if (catId === 0) return '全部笔记'
  const cat = categories.value.find(c => c.id === catId)
  return cat?.name || '全部笔记'
}

// 过滤后的文章
const filteredNotes = computed(() => notes.value)

// 加载分类列表
const loadCategories = async () => {
  isCatLoading.value = true
  try {
    const res = await getCatList()
    if (res.code === 1) {
      categories.value = res.data
    }
  } catch (error) {
    console.error('加载分类失败:', error)
  } finally {
    isCatLoading.value = false
  }
}

// 加载笔记列表
const loadArticles = async () => {
  loading.value = true
  try {
    const res = await getNoteList({
      page: page.value,
      pageSize: pageSize.value,
      cat_id: activeCatId.value || undefined
    })
    if (res.code === 1) {
      notes.value = res.data.list
      total.value = res.data.total
    }
  } catch (error) {
    console.error('加载笔记失败:', error)
  } finally {
    loading.value = false
  }
}

// 加载标签列表
const loadTags = async () => {
  try {
    const res = await getTagList()
    if (res.code === 1) {
      tags.value = res.data
    }
  } catch (error) {
    console.error('加载标签失败:', error)
  }
}

// 监听分类变化，重新加载文章
watch(activeCatId, () => {
  page.value = 1
  loadArticles()
})

// 格式化日期
const formatDate = (timestamp: number) => {
  const date = new Date(timestamp * 1000)
  return `${date.getMonth() + 1}月${date.getDate()}日`
}

// 获取分类名称
const getCatName = (catId: number) => {
  const cat = categories.value.find(c => c.id === catId)
  return cat?.name || '未分类'
}

// 打开新增文章（直接在右侧编辑区开启）
const openAddModal = async () => {
  isEditing.value = false
  isNewArticle.value = false
  selectedNote.value = null
  await nextTick()
  editingNote.value = {
    title: '',
    description: '',
    content: '',
    category_id: activeCatId.value || (categories.value[0]?.id || 0),
    tag_ids: []
  }
  selectedTagIds.value = []
  isEditing.value = true
  isNewArticle.value = true
}

// 选中文章（直接进入编辑模式）
const selectNote = async (note: VentureNote) => {
  isNewArticle.value = false
  isEditing.value = false
  selectedNote.value = note
  isNoteLoading.value = true
  try {
    const res = await getNoteDetail(note.id)
    if (res.code === 1) {
      selectedNote.value = res.data
    }
  } catch (error) {
    console.error('获取笔记详情失败:', error)
  } finally {
    isNoteLoading.value = false
  }
  editingNote.value = { ...selectedNote.value! }
  selectedTagIds.value = selectedNote.value?.tag_ids || []
  isEditing.value = true
}

// 开始编辑当前文章
const startEdit = () => {
  if (!selectedNote.value) return
  editingNote.value = { ...selectedNote.value }
  selectedTagIds.value = selectedNote.value.tag_ids || []
  isEditing.value = true
}

// 取消编辑
const cancelEdit = () => {
  isEditing.value = false
  isNewArticle.value = false
  editingNote.value = {
    title: '',
    description: '',
    content: '',
    category_id: 0,
    tag_ids: []
  }
  selectedTagIds.value = []
}

// 保存文章（右侧编辑区）
const saveNoteEdit = async () => {
  if (!editingNote.value.title?.trim()) {
    alert('请输入文章标题')
    return
  }
  
  isSaving.value = true
  try {
    let articleId = editingNote.value.id
    
    if (articleId) {
      // 编辑
      const res = await editNote(editingNote.value as VentureNote)
      if (res.code !== 1) {
        alert(res.msg || '保存失败')
        return
      }
    } else {
      // 新增
      const res = await addNote(editingNote.value)
      if (res.code !== 1) {
        alert(res.msg || '保存失败')
        return
      }
      articleId = res.data.id
      editingNote.value.id = articleId
    }
    
    // 绑定标签
    if (articleId && selectedTagIds.value.length > 0) {
      await bindTags(articleId, selectedTagIds.value)
    }
    
    // 更新当前文章状态
    if (selectedNote.value) {
      selectedNote.value.title = editingNote.value.title
      selectedNote.value.content = editingNote.value.content
    }
    
    // 显示保存成功提示，不关闭编辑状态
    showToastMessage('保存成功')
  } catch (error) {
    console.error('保存文章失败:', error)
    alert('保存失败')
  } finally {
    isSaving.value = false
  }
}

// 保存文章（弹窗）
const saveNote = async () => {
  if (!editingNote.value.title?.trim()) {
    alert('请输入文章标题')
    return
  }
  
  isSaving.value = true
  try {
    let articleId = editingNote.value.id
    
    // 保存文章
    if (articleId) {
      // 编辑
      const res = await editNote(editingNote.value as VentureNote)
      if (res.code !== 1) {
        alert(res.msg || '保存失败')
        return
      }
    } else {
      // 新增
      const res = await addNote(editingNote.value)
      if (res.code !== 1) {
        alert(res.msg || '保存失败')
        return
      }
      articleId = res.data.id
    }
    
    // 绑定标签
    if (articleId && selectedTagIds.value.length > 0) {
      await bindTags(articleId, selectedTagIds.value)
    }
    
    showEditModal.value = false
    // 显示保存成功提示
    showToastMessage('保存成功')
  } catch (error) {
    console.error('保存文章失败:', error)
    alert('保存失败')
  } finally {
    isSaving.value = false
  }
}

// 确认删除文章
const confirmDelete = (id: number) => {
  deletingNoteId.value = id
  showDeleteConfirm.value = true
}

// 执行删除文章
const doDeleteNote = async () => {
  if (!deletingNoteId.value) return
  
  try {
    const res = await deleteNote(deletingNoteId.value)
    if (res.code === 1) {
      showDeleteConfirm.value = false
      deletingNoteId.value = null
      loadArticles()
      loadCategories()
    } else {
      alert(res.msg || '删除失败')
    }
  } catch (error) {
    console.error('删除文章失败:', error)
    alert('删除失败')
  }
}

// 分类右键菜单
const catContextMenu = ref({
  show: false,
  x: 0,
  y: 0,
  cat: null as VentureNoteCat | null
})

const showCatContextMenu = (event: MouseEvent, cat: VentureNoteCat) => {
  event.preventDefault()
  event.stopPropagation()
  catContextMenu.value = { show: true, x: event.clientX, y: event.clientY, cat }
}

const hideCatContextMenu = () => {
  catContextMenu.value.show = false
  catContextMenu.value.cat = null
}

// 右键 - 编辑分类
const catContextMenuEdit = () => {
  if (catContextMenu.value.cat) {
    openEditCatModal(catContextMenu.value.cat, new Event('click'))
  }
  hideCatContextMenu()
}

// 右键 - 切换分类可见性
const catContextMenuToggleStatus = async () => {
  const cat = catContextMenu.value.cat
  if (!cat) return
  const newStatus = cat.status ? 0 : 1
  try {
    await editCat({ id: cat.id, status: newStatus })
    loadCategories()
  } catch (error) {
    console.error('切换分类状态失败:', error)
  }
  hideCatContextMenu()
}

// 右键 - 删除分类
const catContextMenuDelete = () => {
  if (catContextMenu.value.cat) {
    deletingCatId.value = catContextMenu.value.cat.id
    showDeleteCatConfirm.value = true
  }
  hideCatContextMenu()
}

// 打开添加分类弹窗
const openAddCatModal = () => {
  editingCatId.value = null
  editingCat.value = {
    name: '',
    description: '',
    sort: 0,
    status: 1
  }
  showCatModal.value = true
}

// 打开编辑分类弹窗
const openEditCatModal = (cat: VentureNoteCat, event: Event) => {
  event.stopPropagation()
  editingCatId.value = cat.id
  editingCat.value = { ...cat }
  showCatModal.value = true
}

// 保存分类
const saveCat = async () => {
  if (!editingCat.value.name?.trim()) {
    alert('请输入分类名称')
    return
  }
  
  isSavingCat.value = true
  try {
    if (editingCatId.value) {
      // 编辑
      await editCat({ ...editingCat.value, id: editingCatId.value } as VentureNoteCat)
    } else {
      // 新增
      await addCat(editingCat.value as VentureNoteCat)
    }
    showCatModal.value = false
    loadCategories()
  } catch (error) {
    console.error('保存分类失败:', error)
    alert('保存失败')
  } finally {
    isSavingCat.value = false
  }
}

// 确认删除分类
const confirmDeleteCat = (id: number, event: Event) => {
  event.stopPropagation()
  deletingCatId.value = id
  showDeleteCatConfirm.value = true
}

// 执行删除分类
const doDeleteCat = async () => {
  if (!deletingCatId.value) return
  
  try {
    const res = await deleteCat(deletingCatId.value)
    if (res.code === 1) {
      showDeleteCatConfirm.value = false
      deletingCatId.value = null
      if (activeCatId.value === deletingCatId.value) {
        activeCatId.value = 0
      }
      loadCategories()
      loadArticles()
    } else {
      alert(res.msg || '删除失败')
    }
  } catch (error) {
    console.error('删除分类失败:', error)
    alert('删除失败')
  }
}

// 切换标签选择
const toggleTag = (tagId: number) => {
  const index = selectedTagIds.value.indexOf(tagId)
  if (index > -1) {
    selectedTagIds.value.splice(index, 1)
  } else {
    selectedTagIds.value.push(tagId)
  }
}

onMounted(() => {
  loadCategories()
  loadArticles()
  loadTags()
})
</script>

<template>
  <div class="notes-page">
    <!-- 左侧分类列表 -->
    <aside class="category-sidebar" :class="{ 'sidebar-hidden': !sidebarVisible }">
      <div class="sidebar-header">
        <h3>笔记分类</h3>
        <button class="add-cat-btn" @click="openAddCatModal" title="添加分类">+</button>
      </div>
      <nav class="category-list">
        <!-- 分类加载中 -->
        <div v-if="isCatLoading" class="cat-loading">
          <div class="loading-spinner-small"></div>
        </div>
        
        <!-- 一级分类 -->
        <template v-else v-for="cat in parentCategories" :key="cat.id">
          <button
            class="category-item"
            :class="{ active: activeCatId === cat.id }"
            @click="activeCatId = cat.id"
            @contextmenu="showCatContextMenu($event, cat as VentureNoteCat)"
          >
            <span class="category-name">{{ cat.name }}</span>
            <span class="category-count">{{ cat.count ?? 0 }}</span>
          </button>
        </template>
        
        <div v-if="!isCatLoading && categories.length === 0" class="empty-cats">
          暂无分类
        </div>
      </nav>
    </aside>

    <!-- 分类右键菜单 -->
    <div
      v-if="catContextMenu.show"
      class="context-menu"
      :style="{ left: catContextMenu.x + 'px', top: catContextMenu.y + 'px' }"
    >
      <div class="context-menu-item" @click="catContextMenuToggleStatus">
        <span class="icon">{{ catContextMenu.cat?.status ? '🚫' : '✅' }}</span>
        {{ catContextMenu.cat?.status ? '设为隐藏' : '设为可见' }}
      </div>
      <div class="context-menu-item" @click="catContextMenuEdit">
        <span class="icon">✏️</span> 编辑
      </div>
      <div class="context-menu-divider"></div>
      <div class="context-menu-item danger" @click="catContextMenuDelete">
        <span class="icon">🗑️</span> 删除
      </div>
    </div>

    <!-- 右侧笔记列表 -->
    <main class="notes-content">
      <div class="notes-header">
        <div class="notes-header-left">
          <button class="toggle-sidebar-btn" @click="sidebarVisible = !sidebarVisible" title="显示/隐藏分类">
            ☰
          </button>
          <h2>{{ getCategoryTitle(activeCatId) }}</h2>
        </div>
        <button class="add-note-btn" @click.stop="openAddModal" title="新建笔记">+</button>
      </div>
      
      <div v-if="loading" class="notes-loading">
        <div class="loading-spinner-small"></div>
      </div>
      
      <div v-else-if="filteredNotes.length === 0" class="empty-state">
        暂无笔记，点击右上角按鈕创建
      </div>
      
      <div v-else class="notes-list">
        <div
          v-for="note in filteredNotes"
          :key="note.id"
          class="note-card"
          :class="{ 'note-disabled': note.status === 0 }"
          @click="selectNote(note)"
          @contextmenu="showContextMenu($event, note)"
        >
          <h4 class="note-title">{{ note.title }}</h4>
          <p class="note-preview">{{ note.description || note.content?.replace(/<[^>]+>/g, '').slice(0, 100) || '无内容' }}</p>
          <div class="note-meta">
            <span class="note-date">{{ formatDate(note.update_at) }}</span>
            <span v-if="note.status === 0" class="note-status-hidden">👁️‍🗨️</span>
          </div>
        </div>
      </div>

      <!-- 右键菜单 -->
      <div
        v-if="contextMenu.show"
        class="context-menu"
        :style="{ left: contextMenu.x + 'px', top: contextMenu.y + 'px' }"
      >
        <div class="context-menu-item" @click="contextMenuEdit">
          <span class="icon">✏️</span> 编辑
        </div>
        <div class="context-menu-item" @click="contextMenuToggleStatus">
          <span class="icon">{{ contextMenu.note?.status === 1 ? '👁️' : '🚫' }}</span>
          {{ contextMenu.note?.status === 1 ? '设为隐藏' : '设为可见' }}
        </div>
        <div class="context-menu-divider"></div>
        <div class="context-menu-item danger" @click="contextMenuDelete">
          <span class="icon">🗑️</span> 删除
        </div>
      </div>
    </main>

    <!-- 右侧编辑区域 -->
    <aside class="editor-sidebar">
      <!-- 加载中 -->
      <div v-if="isNoteLoading" class="editor-loading">
        <div class="loading-spinner"></div>
        <p>加载中...</p>
      </div>

      <!-- 新建模式 -->
      <div v-else-if="isNewArticle" class="editor-new">
        <div class="editor-new-header">
          <input
            v-model="editingNote.title"
            class="editor-new-title"
            placeholder="输入标题..."
            autofocus
          />
          <div class="editor-new-actions">
            <button class="btn-save-new" :disabled="isSaving" @click="saveNoteEdit">
              {{ isSaving ? '保存中...' : '保存' }}
            </button>
          </div>
        </div>
        <div class="editor-new-body">
          <MdEditor v-model="editingNote.content" :showSaveButton="false" :noMaxWidth="true" uploadPath="/api/upload/startup" />
        </div>
      </div>

      <!-- 未选中文章 -->
      <div v-else-if="!selectedNote && !isEditing && !isNoteLoading" class="editor-empty">
        <p>请选择一篇文章或创建新笔记</p>
      </div>
      
      <!-- 编辑模式（查看/编辑统一布局） -->
      <div v-else-if="selectedNote && isEditing && !isNoteLoading" class="editor-new">
        <div class="editor-new-header">
          <input
            v-model="editingNote.title"
            class="editor-new-title"
            placeholder="输入标题..."
          />
          <div class="editor-new-actions">
            <button class="btn-save-new" :disabled="isSaving" @click="saveNoteEdit">
              {{ isSaving ? '保存中...' : '保存' }}
            </button>
          </div>
        </div>
        <div class="editor-new-body">
          <MdEditor v-model="editingNote.content" :showSaveButton="false" :noMaxWidth="true" uploadPath="/api/upload/startup" />
        </div>
      </div>
    </aside>

    <!-- 文章编辑弹窗 -->
    <div v-if="showEditModal" class="modal-overlay" @click.self="showEditModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ editingNote.id ? '编辑笔记' : '新建笔记' }}</h3>
          <button class="close-btn" @click="showEditModal = false">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>标题</label>
            <input v-model="editingNote.title" type="text" placeholder="请输入标题" />
          </div>
          <div class="form-group">
            <label>分类</label>
            <select v-model="editingNote.category_id">
              <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>描述</label>
            <input v-model="editingNote.description" type="text" placeholder="简短描述（可选）" />
          </div>
          <div class="form-group">
            <label>标签</label>
            <div class="tag-selector">
              <span
                v-for="tag in tags"
                :key="tag.id"
                class="tag-item"
                :class="{ selected: selectedTagIds.includes(tag.id) }"
                @click="toggleTag(tag.id)"
              >{{ tag.name }}</span>
              <span v-if="tags.length === 0" class="no-tags">暂无标签</span>
            </div>
          </div>
          <div class="form-group">
            <label>内容</label>
            <MdEditor v-model="editingNote.content" :showSaveButton="false" :noMaxWidth="true" uploadPath="/api/upload/startup" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="showEditModal = false">取消</button>
          <button class="btn-primary" :disabled="isSaving" @click="saveNote">
            {{ isSaving ? '保存中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 分类管理弹窗 -->
    <div v-if="showCatModal" class="modal-overlay" @click.self="showCatModal = false">
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h3>{{ editingCatId ? '编辑分类' : '添加分类' }}</h3>
          <button class="close-btn" @click="showCatModal = false">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>标题</label>
            <input v-model="editingCat.name" type="text" placeholder="请输入分类标题" />
          </div>
          <div class="form-group">
            <label>排序</label>
            <input v-model.number="editingCat.sort" type="number" placeholder="请输入排序数字，越小越靠前" />
          </div>
          <div class="form-group">
            <label>是否显示</label>
            <div class="radio-group">
              <label class="radio-label">
                <input type="radio" :value="1" v-model="editingCat.status" />
                <span>显示</span>
              </label>
              <label class="radio-label">
                <input type="radio" :value="0" v-model="editingCat.status" />
                <span>隐藏</span>
              </label>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="showCatModal = false">关闭</button>
          <button class="btn-primary" :disabled="isSavingCat" @click="saveCat">
            {{ isSavingCat ? '保存中...' : (editingCatId ? '更新' : '添加') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 删除文章确认弹窗 -->
    <div v-if="showDeleteConfirm" class="modal-overlay" @click.self="showDeleteConfirm = false">
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h3>确认删除</h3>
        </div>
        <div class="modal-body">
          <p>确定要删除这篇文章吗？此操作不可恢复。</p>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="showDeleteConfirm = false">取消</button>
          <button class="btn-danger" @click="doDeleteNote">删除</button>
        </div>
      </div>
    </div>

    <!-- 删除分类确认弹窗 -->
    <div v-if="showDeleteCatConfirm" class="modal-overlay" @click.self="showDeleteCatConfirm = false">
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h3>确认删除</h3>
        </div>
        <div class="modal-body">
          <p>确定要删除这个分类吗？分类下的文章将变为未分类状态。</p>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="showDeleteCatConfirm = false">取消</button>
          <button class="btn-danger" @click="doDeleteCat">删除</button>
        </div>
      </div>
    </div>

    <!-- Toast 提示 -->
    <div v-if="showToast" class="toast-overlay">
      <div class="toast-message">{{ toastMessage }}</div>
    </div>
  </div>
</template>

<style scoped>
.notes-page {
  display: flex;
  height: 100%;
  background: #f5f5f5;
}

/* 左侧分类栏 */
.category-sidebar {
  width: 240px;
  background: #fff;
  border-right: 1px solid #e0e0e0;
  flex-shrink: 0;
  transition: width 0.25s ease, opacity 0.25s ease;
  overflow: hidden;
}

.category-sidebar.sidebar-hidden {
  width: 0;
  opacity: 0;
  border-right: none;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1rem;
  height: 56px;
  border-bottom: 1px solid #e0e0e0;
}

.sidebar-header h3 {
  margin: 0;
  font-size: 1rem;
  color: #333;
}

.category-list {
  padding: 0.5rem 0;
}

.category-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: background 0.2s;
  min-width: 0;
  overflow: hidden;
}

.category-item:hover {
  background: #f5f5f5;
}

.category-item.active {
  background: #e8f5e9;
  color: #42b883;
}

.category-name {
  font-size: 0.9rem;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
}

.category-count {
  font-size: 0.8rem;
  color: #999;
  background: #f0f0f0;
  padding: 0.1rem 0.5rem;
  border-radius: 10px;
}

.category-item.active .category-count {
  background: #42b883;
  color: #fff;
}

/* 二级分类样式 */
.category-item.category-child {
  padding-left: 1.5rem;
  background: #fafafa;
}

.category-item.category-child:hover {
  background: #f0f0f0;
}

.category-item.category-child.active {
  background: #e8f5e9;
}

.child-indent {
  color: #999;
  margin-right: 0.25rem;
  font-size: 0.8rem;
}

/* 中间文章列表 */
.notes-content {
  width: 320px;
  background: #fff;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.notes-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1rem;
  height: 56px;
  border-bottom: 1px solid #e0e0e0;
}

.notes-header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  overflow: hidden;
  flex: 1;
}

.notes-header h2 {
  margin: 0;
  font-size: 1rem;
  color: #333;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
}

/* 汉堡菜单按鈕 */
.toggle-sidebar-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 4px;
  font-size: 1.2rem;
  color: #555;
  flex-shrink: 0;
  transition: background 0.2s, color 0.2s;
}

.toggle-sidebar-btn:hover {
  background: #f0f0f0;
  color: #333;
}

.add-note-btn {
  width: 28px;
  height: 28px;
  background: #42b883;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1.25rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s;
}

.add-note-btn:hover {
  background: #369970;
}

.notes-list {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
}

.note-card {
  padding: 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
  border-bottom: 1px solid #f0f0f0;
}

.note-card:hover {
  background: #f5f5f5;
}

.note-card.note-active {
  background: #e8f5e9;
}

.note-title {
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
  color: #1a1a2e;
  font-weight: 600;
}

.note-preview {
  margin: 0 0 1rem 0;
  font-size: 0.85rem;
  color: #666;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.note-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.note-category {
  font-size: 0.75rem;
  color: #42b883;
  background: #e8f5e9;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
}

.note-date {
  font-size: 0.75rem;
  color: #999;
}

/* 加载和空状态 */
.loading-state,
.empty-state {
  text-align: center;
  padding: 3rem;
  color: #999;
}

.empty-cats {
  padding: 1rem;
  text-align: center;
  color: #999;
  font-size: 0.85rem;
}

/* 删除按钮 */
.delete-btn {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 24px;
  height: 24px;
  border: none;
  background: #ff4d4f;
  color: #fff;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  opacity: 0;
  transition: opacity 0.2s;
}

.note-card:hover .delete-btn {
  opacity: 1;
}

.delete-btn:hover {
  background: #ff7875;
}

/* 添加分类按钮 */
.add-cat-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: #42b883;
  color: #fff;
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.add-cat-btn:hover {
  background: #369970;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-content.modal-small {
  max-width: 500px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e0e0e0;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.1rem;
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #999;
}

.close-btn:hover {
  color: #333;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e0e0e0;
}

/* 表单样式 */
.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: #333;
  font-weight: 500;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 0.9rem;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #42b883;
}

/* 标签选择器 */
.tag-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tag-item {
  padding: 0.25rem 0.75rem;
  background: #f0f0f0;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s;
}

.tag-item:hover {
  background: #e0e0e0;
}

.tag-item.selected {
  background: #42b883;
  color: #fff;
}

.no-tags {
  color: #999;
  font-size: 0.8rem;
}

/* 分类列表 */
.cat-list {
  margin-bottom: 1rem;
  max-height: 200px;
  overflow-y: auto;
}

.cat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  border-bottom: 1px solid #f0f0f0;
}

.cat-name {
  font-size: 0.9rem;
}

.cat-actions {
  display: flex;
  gap: 0.5rem;
}

.cat-actions button {
  padding: 0.2rem 0.5rem;
  font-size: 0.75rem;
  border: none;
  background: #f0f0f0;
  border-radius: 4px;
  cursor: pointer;
}

.cat-actions button:hover {
  background: #e0e0e0;
}

/* 单选按钮组 */
.radio-group {
  display: flex;
  gap: 1.5rem;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.9rem;
  color: #333;
}

.radio-label input[type="radio"] {
  width: auto;
  cursor: pointer;
}

/* 按钮样式 */
.btn-secondary,
.btn-primary,
.btn-danger {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s;
}

.btn-secondary {
  background: #f0f0f0;
  color: #666;
}

.btn-secondary:hover {
  background: #e0e0e0;
}

.btn-primary {
  background: #42b883;
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background: #369970;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-danger {
  background: #ff4d4f;
  color: #fff;
}

.btn-danger:hover {
  background: #ff7875;
}

/* 右键菜单 */
.context-menu {
  position: fixed;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  padding: 0.5rem 0;
  min-width: 140px;
  z-index: 2000;
}

.context-menu-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  cursor: pointer;
  font-size: 0.9rem;
  color: #333;
  transition: background 0.2s;
}

.context-menu-item:hover {
  background: #f5f5f5;
}

.context-menu-item.danger {
  color: #ff4d4f;
}

.context-menu-item.danger:hover {
  background: #fff1f0;
}

.context-menu-item .icon {
  font-size: 0.9rem;
}

.context-menu-divider {
  height: 1px;
  background: #e8e8e8;
  margin: 0.4rem 0;
}

/* 隐藏状态的笔记 */
.note-disabled {
  opacity: 0.6;
}

.note-status-hidden {
  font-size: 0.7rem;
  color: #999;
  background: #f0f0f0;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  margin-left: 0.5rem;
}

/* 右侧编辑区域 */
.editor-sidebar {
  flex: 1;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.editor-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
}

/* 加载中 */
.editor-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #999;
  gap: 1rem;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f0f0f0;
  border-top-color: #42b883;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* 小加载动画 */
.loading-spinner-small {
  width: 24px;
  height: 24px;
  border: 2px solid #f0f0f0;
  border-top-color: #42b883;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* 分类加载中 */
.cat-loading {
  display: flex;
  justify-content: center;
  padding: 2rem 0;
}

/* 笔记列表加载中 */
.notes-loading {
  display: flex;
  justify-content: center;
  padding: 3rem 0;
}

/* 新建模式 */
.editor-new {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.editor-new-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  gap: 1rem;
}

.editor-new-title {
  flex: 1;
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a2e;
  border: none;
  outline: none;
  background: transparent;
}

.editor-new-title::placeholder {
  color: #ccc;
  font-weight: 400;
}

.editor-new-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.new-cat-select {
  padding: 0.4rem 0.6rem;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 0.85rem;
  color: #555;
  background: #fff;
  cursor: pointer;
}

.new-cat-select:focus {
  outline: none;
  border-color: #42b883;
}

.btn-save-new {
  padding: 0.5rem 1.25rem;
  background: #42b883;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-save-new:hover:not(:disabled) {
  background: #369970;
}

.btn-save-new:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.editor-new-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* 查看模式 */
.editor-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.editor-view-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e0e0e0;
}

.editor-view-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.view-cat {
  font-size: 0.8rem;
  color: #42b883;
  background: #e8f5e9;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
}

.view-date {
  font-size: 0.8rem;
  color: #999;
}

.view-status {
  font-size: 0.75rem;
  color: #999;
  background: #f0f0f0;
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
}

.editor-view-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-edit,
.btn-delete {
  padding: 0.4rem 0.75rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s;
}

.btn-edit {
  background: #42b883;
  color: #fff;
}

.btn-edit:hover {
  background: #369970;
}

.btn-delete {
  background: #ff4d4f;
  color: #fff;
}

.btn-delete:hover {
  background: #ff7875;
}

.editor-view-title {
  margin: 0;
  padding: 1.5rem 1.5rem 0.5rem;
  font-size: 1.5rem;
  color: #333;
  font-weight: 600;
}

.editor-view-desc {
  padding: 0 1.5rem 1rem;
  color: #666;
  font-size: 0.95rem;
  border-bottom: 1px solid #f0f0f0;
}

.editor-view-content {
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
  line-height: 1.8;
}

.editor-view-content :deep(p) {
  margin: 0.75rem 0;
}

.editor-view-content :deep(h1),
.editor-view-content :deep(h2),
.editor-view-content :deep(h3) {
  margin: 1.5rem 0 0.75rem;
}

.editor-view-content :deep(ul),
.editor-view-content :deep(ol) {
  margin: 0.75rem 0;
  padding-left: 1.5rem;
}

.editor-view-content :deep(img) {
  max-width: 100%;
  border-radius: 4px;
}

/* 编辑模式 */
.editor-edit {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.editor-edit-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e0e0e0;
}

.form-row {
  margin-bottom: 0.75rem;
}

.form-row:last-child {
  margin-bottom: 0;
}

.form-row-inline {
  display: flex;
  gap: 0.75rem;
}

.edit-title {
  width: 100%;
  padding: 0.5rem 0;
  border: none;
  border-bottom: 2px solid #e0e0e0;
  font-size: 1.25rem;
  font-weight: 600;
  outline: none;
  transition: border-color 0.2s;
}

.edit-title:focus {
  border-color: #42b883;
}

.edit-select {
  width: 120px;
  padding: 0.4rem 0.5rem;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 0.85rem;
}

.edit-desc {
  flex: 1;
  padding: 0.4rem 0.5rem;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 0.85rem;
}

.editor-edit-body {
  flex: 1;
  padding: 1rem 1.5rem;
  overflow-y: auto;
}

.editor-edit-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e0e0e0;
}

/* Toast 提示 */
.toast-overlay {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  animation: toastSlideDown 0.3s ease;
}

@keyframes toastSlideDown {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.toast-message {
  background: #42b883;
  color: #fff;
  padding: 12px 32px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  box-shadow: 0 4px 16px rgba(66, 184, 131, 0.4);
}
</style>
